import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import client, {
  readToken,
  setUnauthorizedHandler,
  writeToken,
} from "../../api/client";
import endpoints from "../../api/endpoints";

const AuthContext = createContext(null);

// The OTP flow spans two pages. The old UserContext kept the phone and name in
// memory only, so reloading on the verify step silently sent `phone: ""` and
// the code could never match. Session storage survives the reload and is
// cleared once the flow completes.
const DRAFT_KEY = "auth.draft";

const readDraft = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(DRAFT_KEY)) ?? {};
  } catch {
    return {};
  }
};

const writeDraft = (draft) => {
  try {
    if (draft) window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    else window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* storage blocked — the flow still works without a reload */
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readToken());
  const [draft, setDraft] = useState(readDraft);

  const clearSession = useCallback(() => {
    writeToken(null);
    setToken(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  const saveDraft = useCallback((patch) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      writeDraft(next);
      return next;
    });
  }, []);

  const finishAuth = useCallback((newToken) => {
    writeToken(newToken);
    setToken(newToken);
    writeDraft(null);
    setDraft({});
  }, []);

  /** Step 1 of login: ask the backend to text a code. */
  const requestLoginCode = useCallback(
    async (phone) => {
      await client.post(endpoints.sendLoginCode(), { phone });
      saveDraft({ phone });
    },
    [saveDraft]
  );

  /** Step 2 of login: exchange the code for a token. */
  const verifyLogin = useCallback(
    async (code) => {
      const { data } = await client.post(endpoints.loginToken(), {
        code,
        phone: draft.phone,
      });
      finishAuth(data.token);
    },
    [draft.phone, finishAuth]
  );

  /** Step 1 of sign-up. */
  const requestRegisterCode = useCallback(
    async ({ phone, firstName, lastName }) => {
      await client.post(endpoints.sendRegisterCode(), { phone });
      saveDraft({ phone, firstName, lastName });
    },
    [saveDraft]
  );

  /** Step 2 of sign-up: check the code, then register with the returned token. */
  const verifyRegister = useCallback(
    async (code) => {
      const { data } = await client.post(endpoints.checkCode(), {
        phone: draft.phone,
        code,
      });
      const { data: registered } = await client.post(endpoints.register(), {
        phone: draft.phone,
        verification_token: data.verification_token,
        first_name: draft.firstName,
        last_name: draft.lastName,
      });
      finishAuth(registered.token);
    },
    [draft, finishAuth]
  );

  const value = useMemo(
    () => ({
      token,
      phone: draft.phone ?? "",
      isAuthenticated: Boolean(token),
      requestLoginCode,
      verifyLogin,
      requestRegisterCode,
      verifyRegister,
      logOut: clearSession,
    }),
    [
      token,
      draft.phone,
      requestLoginCode,
      verifyLogin,
      requestRegisterCode,
      verifyRegister,
      clearSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
};
